import { App } from 'antd'
import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'
import type { NavigateFunction } from 'react-router-dom'

import { type SocialProviderId, providerService } from '../providers'

type UseProviderCallbackParams = {
  provider: SocialProviderId
  flow: 'login' | 'connect' | 'clientConnect' | 'orgConnect'
  searchParams: URLSearchParams
  navigate: NavigateFunction
  t: TFunction
  missingParamsKey: string
  fallbackPath: string
  isBlocked?: boolean
  onProcess: (params: {
    code: string
    state: string
    returnOrigin: string | null
    user: string | null
  }) => Promise<void>
}

const resolveRelayOrigin = (returnOrigin: string): string | null => {
  try {
    return new URL(returnOrigin).origin
  } catch {
    return null
  }
}

export const useProviderCallback = ({
  provider,
  flow,
  searchParams,
  navigate,
  t,
  missingParamsKey,
  fallbackPath,
  isBlocked = false,
  onProcess
}: UseProviderCallbackParams) => {
  const { message } = App.useApp()
  const exchangedRef = useRef(false)

  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const user = searchParams.get('user')

    if (!code || !state) {
      message.error(t(missingParamsKey))
      navigate(fallbackPath)
      return
    }

    if (isBlocked || exchangedRef.current) return
    exchangedRef.current = true

    void (async () => {
      let returnOrigin: string | null = null

      try {
        returnOrigin = await providerService.getStateInfo(provider, state)
      } catch {
        message.error(t(missingParamsKey))
        navigate(fallbackPath)
        return
      }

      const relayOrigin = returnOrigin ? resolveRelayOrigin(returnOrigin) : null

      if (relayOrigin && relayOrigin !== window.location.origin) {
        const query = searchParams.toString()
        const callbackPath = providerService.getCallbackPath(provider, flow)
        window.location.href = query
          ? `${relayOrigin}${callbackPath}?${query}`
          : `${relayOrigin}${callbackPath}`
        return
      }

      await onProcess({ code, state, returnOrigin, user })
    })()
  }, [
    provider,
    flow,
    searchParams,
    navigate,
    t,
    missingParamsKey,
    fallbackPath,
    isBlocked,
    onProcess,
    message
  ])
}
